import React from "react";
import { useQuery } from "@apollo/client";
import { GET_ME } from "../utils/mutations";

/**
 * Drop-in futuristic badge UI for ID.me data.
 * Updates:
 * - Group chips moved INSIDE the verified/unverified pill area (only shown when Verified)
 * - Hover tooltip description shortened to JUST the label
 * - Keeps structure/styling compact, dev dummy data, multi-category dedupe
 */
const UserBadge = (props) => {
  const {
    user: propUser,
    idme: propIdme,
    iconColor: propIconColor,
    compact = false,
  } = props;

  // ---- env / mode detection (Vite, CRA, Next) ----
  const MODE =
    (typeof import.meta !== "undefined" && import.meta?.env?.MODE) ||
    process.env.REACT_APP_ENV ||
    process.env.NEXT_PUBLIC_ENV ||
    process.env.NODE_ENV ||
    "development";

  // const isDev = String(MODE).toLowerCase() !== "production";
  const isDev = false; // force prod mode for testing without dummy data
  // ---- dummy data for development (only used if props not provided) ----
  const devDummy = {
    user: "dev_user",
    iconColor: "#462b65",
    idme: {
      status: [
        // { group: "teacher", verified: true, issuedAt: "2025-11-07" },
        { group: "military", verified: true, issuedAt: "2024-03-12" },
        // { group: "first responder", verified: false, issuedAt: null },
      ],
    },
  };

  // Skip query in dev or if props provided.
  const { loading, error, data } = useQuery(GET_ME, {
    skip: !!(propUser && propIdme) || isDev,
  });

  if (!isDev && loading) return <p>Loading...</p>;
  if (!isDev && error) return <p>Error loading user data: {error.message}</p>;

  // Resolve data
  const idme = propIdme
    ? propIdme
    : isDev
    ? devDummy.idme
    : data?.me?.idme;

  const user = propUser
    ? propUser
    : isDev
    ? devDummy.user
    : data?.me?.username;

  const color =
    propIconColor ||
    data?.me?.iconColor ||
    (isDev ? devDummy.iconColor : null) ||
    "#000";

  const rawStatuses = Array.isArray(idme?.status) ? idme.status : [];
  const isVerified = rawStatuses.some((s) => !!s?.verified);

  // Group → label tuning
  const getGroupMeta = (groupRaw) => {
    const group = (groupRaw || "").toLowerCase().trim();
    switch (group) {
      case "military":
        return { label: "Military", accent: "#2e8a00ff", emoji: "🎖️" };
      case "teacher":
        return { label: "Teacher", accent: "#d3be34ff", emoji: "👩‍🏫" };
      case "first responder":
      case "first-responder":
      case "first_responder":
        return { label: "First Responder", accent: "#b61a31ff", emoji: "🚑" };
      case "nurse":
      case "healthcare":
        return { label: "Healthcare", accent: "#a78bfa", emoji: "🩺" };
      case "student":
        return { label: "Student", accent: "#fbbf24", emoji: "🎓" };
      default:
        return { label: groupRaw || "Unknown", accent: "#94a3b8", emoji: "❓" };
    }
  };

  // Multi-category handling: dedupe by normalized label; prefer verified=true
  const groupMap = new Map();
  for (const s of rawStatuses) {
    const meta = getGroupMeta(s?.group);
    const key = meta.label.toLowerCase();
    const existing = groupMap.get(key);

    if (!existing) groupMap.set(key, s);
    else if (!existing.verified && s?.verified) groupMap.set(key, s);
  }

  // Stable ordering: verified first, then alpha label
  const statuses = Array.from(groupMap.values()).sort((a, b) => {
    const av = a?.verified ? 1 : 0;
    const bv = b?.verified ? 1 : 0;
    if (av !== bv) return bv - av;
    return getGroupMeta(a?.group).label
      .toLowerCase()
      .localeCompare(getGroupMeta(b?.group).label.toLowerCase());
  });

  const borderGlow = `0 0 0 1px rgba(255,255,255,0.12), 0 0 22px ${color}40`;
  const cardBg =
    "linear-gradient(135deg, rgba(15,23,42,0.70), rgba(2,6,23,0.55))";
  const wrapperClass = compact
    ? "inline-flex items-center gap-1 rounded-2xl px-2 py-1"
    : "inline-flex items-center gap-1.5 rounded-2xl px-2.5 py-1.5";
  const nameClass = compact
    ? "text-[10px] font-semibold tracking-wide text-slate-100"
    : "text-[11px] font-semibold tracking-wide text-slate-100";
  const verifiedPillClass = compact
    ? "ml-1 inline-flex items-center gap-1 rounded-full px-1 py-[1px] text-[8px] font-semibold uppercase tracking-wide"
    : "ml-1 inline-flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[9px] font-semibold uppercase tracking-wide";
  const groupChipClass = compact
    ? "inline-flex items-center gap-1 rounded-full px-1 py-[1px] text-[8px] font-semibold tracking-wide"
    : "inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] text-[9px] font-semibold tracking-wide";
  const unverifiedPillClass = compact
    ? "ml-1 inline-flex items-center rounded-full px-1.5 py-[2px] text-[8px] font-semibold uppercase tracking-wide"
    : "ml-1 inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold uppercase tracking-wider";
  const statusDotSize = compact ? 4 : 5;

  return (
    <div
      className={wrapperClass}
      style={{
        background: cardBg,
        boxShadow: borderGlow,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      {/* Left: identity dot + username */}
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{
            backgroundColor: color,
            boxShadow: `0 0 0 2px rgba(255,255,255,0.10), 0 0 14px ${color}55`,
          }}
        />
        <span className={nameClass}>
          <span className="text-slate-50">{user || "unknown"}</span>
        </span>
      </div>

      {/* Verified pill (now contains badges) */}
      {isVerified ? (
        <span
          className={verifiedPillClass}
          style={{
            background:
              "linear-gradient(135deg, rgba(16,185,129,0.25), rgba(34,197,94,0.12))",
            boxShadow:
              "0 0 0 1px rgba(34,197,94,0.25), 0 0 14px rgba(34,197,94,0.20)",
            color: "rgba(187,247,208,0.95)",
          }}
          title="ID.me verification present"
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: statusDotSize,
              height: statusDotSize,
              borderRadius: 999,
              background: "rgba(34,197,94,0.9)",
              boxShadow: "0 0 10px rgba(34,197,94,0.75)",
            }}
          />
          <span>Verified</span>

          {/* Group chips INSIDE the pill */}
          {Array.isArray(statuses) && statuses.length > 0 ? (
            <span className="ml-1 inline-flex flex-wrap items-center gap-1">
              {statuses.map((status, index) => {
                if (!status?.verified) return null; // show only verified groups
                const meta = getGroupMeta(status?.group);

                return (
                  <span key={index} className="relative group">
                    <span
                      className={groupChipClass}
                      style={{
                        background: meta.accent,
                        boxShadow: `0 0 0 1px rgba(255,255,255,0.10), 0 0 14px ${meta.accent}33`,
                        color: "rgba(241,245,249,0.92)",
                        border: "1px solid rgba(255,255,255,0.12)",
                      }}
                    >
                      <span>{meta.emoji}</span>
                    </span>

                    {/* Hover tooltip: label only */}
                    <span
                      className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg px-2 py-1 text-[10px] font-medium text-slate-100 shadow-lg group-hover:block"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(15,23,42,0.92), rgba(2,6,23,0.90))",
                        boxShadow:
                          "0 0 0 1px rgba(255,255,255,0.10), 0 10px 30px rgba(0,0,0,0.35)",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                      }}
                    >
                      {meta.label}
                      <span
                        aria-hidden="true"
                        className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45"
                        style={{
                          background: "rgba(15,23,42,0.92)",
                          boxShadow: "0 0 0 1px rgba(255,255,255,0.08)",
                        }}
                      />
                    </span>
                  </span>
                );
              })}
            </span>
          ) : null}
        </span>
      ) : (
        <span
          className={unverifiedPillClass}
          style={{
            background:
              "linear-gradient(135deg, rgba(148,163,184,0.18), rgba(148,163,184,0.08))",
            boxShadow:
              "0 0 0 1px rgba(148,163,184,0.18), 0 0 12px rgba(148,163,184,0.10)",
            color: "rgba(226,232,240,0.85)",
          }}
          title="No verified ID.me status found"
        >
          Unverified
        </span>
      )}
    </div>
  );
};

export default UserBadge;
